import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal, BULAN, uploadFile, fileUrl } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, X, Send, Upload, Paperclip } from "lucide-react";

const THIS_YEAR = new Date().getFullYear();
const TAHUN_OPTS = Array.from({ length: 5 }, (_, i) => String(THIS_YEAR - i));

export function BukuPenghubungGuru() {
  const [items, setItems] = useState([]);
  const [pend, setPend] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ siswa_id: "", catatan_guru: "", file_id: null, file_name: "" });
  const [uploading, setUploading] = useState(false);
  const [fSiswa, setFSiswa] = useState("");
  const [fTgl, setFTgl] = useState("");
  const [fBulan, setFBulan] = useState("");
  const [fTahun, setFTahun] = useState("");

  const [applied, setApplied] = useState({ siswa: "", tgl: "", bulan: "", tahun: "" });
  const load = useCallback(async () => {
    const params = {};
    if (applied.siswa) params.siswa_id = applied.siswa;
    if (applied.tgl) params.tanggal = applied.tgl;
    if (applied.bulan) params.bulan = applied.bulan;
    if (applied.tahun) params.tahun = applied.tahun;
    const [{ data: bp }, { data: p }] = await Promise.all([
      api.get("/buku-penghubung", { params }),
      api.get("/pendampingan"),
    ]);
    setItems(bp); setPend(p);
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied({ siswa: fSiswa, tgl: fTgl, bulan: fBulan, tahun: fTahun });
  const reset = () => { setFSiswa(""); setFTgl(""); setFBulan(""); setFTahun(""); setApplied({ siswa: "", tgl: "", bulan: "", tahun: "" }); };

  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const res = await uploadFile(f);
      setForm({ ...form, file_id: res.id, file_name: f.name });
      toast.success("File terupload");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/buku-penghubung", form);
      toast.success("Catatan terkirim");
      setShow(false);
      setForm({ siswa_id: "", catatan_guru: "", file_id: null, file_name: "" });
      load();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Buku Penghubung" action={<button onClick={() => setShow(true)} className="pp-btn-primary" data-testid="open-bp-form"><Plus className="w-4 h-4" />Tulis Catatan</button>} />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-5 gap-3">
          <select className="pp-input" value={fSiswa} onChange={(e) => setFSiswa(e.target.value)} data-testid="bp-filter-siswa">
            <option value="">Semua siswa</option>
            {pend.map(p => <option key={p.siswa_id} value={p.siswa_id}>{p.nama_siswa}</option>)}
          </select>
          <input type="date" className="pp-input" value={fTgl} onChange={(e) => setFTgl(e.target.value)} data-testid="bp-filter-tanggal" />
          <select className="pp-input" value={fBulan} onChange={(e) => setFBulan(e.target.value)} data-testid="bp-filter-bulan">
            <option value="">Semua bulan</option>
            {BULAN.map(b => <option key={b.v} value={b.v}>{b.label}</option>)}
          </select>
          <select className="pp-input" value={fTahun} onChange={(e) => setFTahun(e.target.value)} data-testid="bp-filter-tahun">
            <option value="">Semua tahun</option>
            {TAHUN_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="bp-filter-apply">Terapkan</button>
            <button onClick={reset} className="pp-btn-secondary flex-1" data-testid="bp-filter-reset">Reset</button>
          </div>
        </div>
      </section>

      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold">Buku Penghubung — Untuk Orangtua</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="space-y-4">
            <div><label className="pp-label">Siswa *</label>
              <select required className="pp-input" value={form.siswa_id} onChange={(e) => setForm({...form, siswa_id: e.target.value})}>
                <option value="">Pilih siswa</option>{pend.map(p => <option key={p.siswa_id} value={p.siswa_id}>{p.nama_siswa}</option>)}
              </select></div>
            <div><label className="pp-label">Catatan untuk Orangtua *</label><textarea required rows={5} className="pp-input" value={form.catatan_guru} onChange={(e) => setForm({...form, catatan_guru: e.target.value})} /></div>
            <div>
              <label className="pp-label">Lampiran (gambar / PDF / DOCX) — opsional</label>
              <label className="pp-btn-secondary cursor-pointer inline-flex" data-testid="bp-upload-label">
                <Upload className="w-4 h-4" /> {uploading ? "Mengunggah…" : (form.file_id ? "Ganti File" : "Pilih File")}
                <input type="file" accept="image/*,.pdf,.docx,.doc" onChange={handleFileUpload} className="hidden" />
              </label>
              {form.file_name && (
                <span className="ml-3 inline-flex items-center gap-1 text-sm text-emerald-600">
                  <Paperclip className="w-3.5 h-3.5" /> {form.file_name}
                </span>
              )}
            </div>
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button><button type="submit" className="pp-btn-primary"><Send className="w-4 h-4" />Kirim</button></div>
          </form>
        </section>
      )}
      <section className="space-y-3">
        {items.length === 0 && <div className="pp-card p-10 text-center italic text-slate-400">Belum ada catatan.</div>}
        {items.map(b => (
          <article key={b.id} className="pp-card p-5">
            <div className="text-xs text-slate-500 mb-1">{formatTanggal(b.created_at)} — {b.nama_siswa}</div>
            <p className="text-slate-800 whitespace-pre-wrap mb-3">{b.catatan_guru}</p>
            {b.file_id && (
              <a href={fileUrl(b.file_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 mb-3" data-testid={`bp-attachment-${b.id}`}>
                <Paperclip className="w-3.5 h-3.5" /> {b.file_name || "Lihat lampiran"}
              </a>
            )}
            {b.balasan_ortu ? (
              <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="text-[11px] uppercase tracking-wider text-indigo-700 font-semibold mb-1">Balasan Orangtua</div>
                <p className="text-slate-800 text-sm whitespace-pre-wrap">{b.balasan_ortu}</p>
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">Belum ada balasan dari orangtua.</div>
            )}
          </article>
        ))}
      </section>
    </Layout>
  );
}
